import keyBy from 'lodash/keyBy';
import Vue from 'vue';

import { saveWrap, getKey } from './helpers';
import * as questionApi from '@authoringPlatform/../api/questions';
import * as fileApi from '@authoringPlatform/../api/files';
import * as questionGroupApi from '@authoringPlatform/../api/questionGroups';
import * as answerApi from '@authoringPlatform/../api/answers';
import { isMilestoneAchieved } from '@authoringPlatform/../utils/iapValidation';

const getDefaultState = () => ({
  questions: {},
  questionGroups: {},
});

export default {
  namespaced: true,

  state: getDefaultState(),

  actions: {
    addQuestion: saveWrap(async ({ commit, dispatch, rootState, state }, { displayTypeKey }) => {
      // Count logic is needed in order to generate the unique key for each question
      // TODO: Move the count logic to backend
      /* Count logic starts */
      const versionConfig = rootState.editMode.version.versionConfig;

      let questionTypeCount = versionConfig.questionTypeCount;

      if (!('questionTypeCount' in versionConfig)) {
        questionTypeCount = Object.values(state.questions).reduce(
          (acc, { FK_questionDisplayTypeKey }) => {
            acc[FK_questionDisplayTypeKey] += 1;
            return acc;
          },
          { 1: 0, 2: 0 }
        );
      }
      questionTypeCount[displayTypeKey] += 1;

      await dispatch(
        'editMode/version/updateVersion',
        {
          payload: {
            versionConfigJson: {
              ...versionConfig,
              questionTypeCount,
            },
          },
        },
        { root: true }
      );

      const count = questionTypeCount[displayTypeKey];
      const versionKey = rootState.editMode.version.versionKey;
      const prefix = displayTypeKey === 2 ? 'IM' : 'E';
      const questionGroupPayload = {
        FK_versionKey: versionKey,
        isDefault: 0,
        name: `${prefix}-${count}`,
      };
      /* Count logic ends */

      const newQuestionGroup = await questionGroupApi.addQuestionGroup({
        payload: questionGroupPayload,
      });

      commit('ADD_QUESTION_GROUP', {
        questionGroupKey: newQuestionGroup.questionGroupKey,
        newQuestionGroup,
      });

      const defaultCharacter = Object.values(rootState.editMode.characters.characters)[0];

      const questionPayload = {
        FK_questionDisplayTypeKey: displayTypeKey,
        FK_authorKey: defaultCharacter.authorKey,
        isImportant: 0,
        timer: 0,
        descriptionTagKey: '',
        subjectTagKey: '',
        FK_questionGroupKey: null,
        isWrittenResponse: 0,
        FK_questionTypeKey: 1,
        isSmartThreading: 0,
        FK_questionGroupKey: newQuestionGroup.questionGroupKey,
        versionKey,
      };

      const newQuestion = await questionApi.addQuestion({ payload: questionPayload });

      commit('ADD_QUESTION', {
        questionKey: newQuestion.questionKey,
        newQuestion,
        newQuestionGroup,
        character: defaultCharacter,
      });

      return newQuestion;
    }),

    saveQuestion: saveWrap(async ({ commit, state, rootState }, payload) => {
      await questionApi.updateQuestion({ questionKey: payload.questionKey, payload });

      const newAuthorKey = payload.FK_authorKey;
      const oldAuthorKey = state.questions[payload.questionKey].FK_authorKey;

      if (newAuthorKey !== oldAuthorKey) {
        commit(
          'editMode/characters/REMOVE_QUESTION_FROM_AUTHOR',
          {
            authorKey: oldAuthorKey,
            questionKey: payload.questionKey,
          },
          { root: true }
        );
        commit(
          'editMode/characters/ADD_QUESTION_TO_AUTHOR',
          {
            authorKey: newAuthorKey,
            questionKey: payload.questionKey,
          },
          { root: true }
        );
      }

      const character = rootState.editMode.characters.characters[payload.FK_authorKey];
      commit('UPDATE_QUESTION', { questionKey: payload.questionKey, payload, character });
    }),

    deleteQuestion: saveWrap(async ({ commit, state }, { questionKey }) => {
      const targetQuestion = state.questions[questionKey];
      await questionApi.deleteQuestion({ questionKey });
      // Removing relationships
      Object.values(targetQuestion.answers).forEach((a) => {
        // Delete answer dependants
        Object.keys(a.dependants).forEach((questionKey) => {
          commit('DELETE_QUESTION_DEPENDENCY', {
            answerKey: a.answerKey,
            questionKey,
          });
        });
        // Delete answers from skills
        Object.keys(a.answerToSkill).forEach((skillKey) => {
          commit(
            'editMode/skills/DELETE_ANSWER_FROM_SKILL',
            {
              skillKey,
              answerKey: a.answerKey,
            },
            { root: true }
          );
        });
      });
      // Delete question dependencies
      // TODO: refactor this so the lookup's less time consuming
      Object.values(targetQuestion.dependsOn).forEach(({ FK_answerKey }) => {
        Object.values(state.questions).forEach((q) => {
          if (FK_answerKey in q.answers) {
            commit('DELETE_ANSWER_DEPENDANT', {
              questionKey: q.questionKey,
              depKey: questionKey,
              answerKey: FK_answerKey,
            });
          }
        });
      });
      commit('DELETE_QUESTION', { questionKey });
    }),

    duplicateQuestion: saveWrap(async ({ commit, state, dispatch, rootState }, { questionKey }) => {
      // Count logic is needed in order to generate the unique key for each question
      // TODO: Move the count logic to backend
      /* Count logic starts */
      const displayTypeKey = state.questions[questionKey].FK_questionDisplayTypeKey;
      const versionConfig = rootState.editMode.version.versionConfig;

      let questionTypeCount = versionConfig.questionTypeCount;

      if (!('questionTypeCount' in versionConfig)) {
        questionTypeCount = Object.values(state.questions).reduce(
          (acc, { FK_questionDisplayTypeKey }) => {
            acc[FK_questionDisplayTypeKey] += 1;
            return acc;
          },
          { 1: 0, 2: 0 }
        );
      }
      questionTypeCount[displayTypeKey] += 1;

      await dispatch(
        'editMode/version/updateVersion',
        {
          payload: {
            versionConfigJson: {
              ...versionConfig,
              questionTypeCount,
            },
          },
        },
        { root: true }
      );

      const count = questionTypeCount[displayTypeKey];
      const versionKey = rootState.editMode.version.versionKey;
      const prefix = displayTypeKey === 2 ? 'IM' : 'E';
      const questionGroupPayload = {
        FK_versionKey: versionKey,
        isDefault: 0,
        name: `${prefix}-${count}`,
      };
      /* Count logic ends */

      const newQuestionGroup = await questionGroupApi.addQuestionGroup({
        payload: questionGroupPayload,
      });

      commit('ADD_QUESTION_GROUP', {
        questionGroupKey: newQuestionGroup.questionGroupKey,
        newQuestionGroup,
      });

      const newQuestion = await questionApi.duplicateQuestion({
        payload: { questionKey, FK_questionGroupKey: newQuestionGroup.questionGroupKey },
      });

      commit('ADD_QUESTION', {
        questionKey: newQuestion.questionKey,
        newQuestion,
        newQuestionGroup,
        character: rootState.editMode.characters.characters[newQuestion.FK_authorKey],
      });

      return newQuestion;
    }),

    switchQuestionTypeAndReset: saveWrap(
      async (
        { commit, state, rootState },
        { questionKey, FK_questionTypeKey, FK_questionGroupKey, FK_authorKey }
      ) => {
        const payload = { FK_questionTypeKey, FK_questionGroupKey };
        await questionApi.updateQuestion({ questionKey, payload });
        const character = rootState.editMode.characters.characters[FK_authorKey];
        commit('UPDATE_QUESTION', { questionKey, payload, character });

        // Delete relationships
        Object.values(state.questions[questionKey].answers).forEach(
          ({ answerKey, dependants, answerToSkill }) => {
            Object.keys(dependants).forEach((questionKey) => {
              commit('DELETE_QUESTION_DEPENDENCY', { questionKey, answerKey });
            });
            Object.keys(answerToSkill).forEach((skillKey) => {
              commit(
                'editMode/skills/DELETE_ANSWER_FROM_SKILL',
                { skillKey, answerKey },
                { root: true }
              );
            });
          }
        );

        if (FK_questionTypeKey === 3) {
          await questionApi.clearAnswersFromQuestion({ questionKey });
          commit('CLEAR_ANSWERS_FROM_QUESTION', { questionKey });
        }
      }
    ),

    addFileToQuestion: saveWrap(
      async (
        { commit, rootState, dispatch },
        { payload, attachmentTooltip, firstEmailTooltip, emailType }
      ) => {
        try {
          const newFile = await fileApi.addFileToQuestion(payload);

          if (emailType === 1) {
            if (!isMilestoneAchieved(rootState.milestones, 5)) {
              await dispatch(
                'SET_MILESTONE',
                {
                  message: 'Create First Email',
                  tooltipDesc: firstEmailTooltip,
                  timeout: 5000,
                  milestoneKey: 5,
                },
                { root: true }
              );
              setTimeout(async () => {
                await dispatch(
                  'SET_MILESTONE',
                  {
                    message: 'First Attachment',
                    tooltipDesc: attachmentTooltip,
                    timeout: 8000,
                    milestoneKey: 11,
                  },
                  { root: true }
                );
              }, 10000);
            } else {
              await dispatch(
                'SET_MILESTONE',
                {
                  message: 'First Attachment',
                  tooltipDesc: attachmentTooltip,
                  timeout: 8000,
                  milestoneKey: 11,
                },
                { root: true }
              );
            }
          }

          commit('ADD_FILE_TO_QUESTION', {
            questionKey: payload.FK_questionKey,
            newFile: newFile.file,
          });
        } catch (err) {
          return err;
        }
      }
    ),

    deleteFileFromQuestion: saveWrap(async ({ commit }, { fileKey, questionKey }) => {
      await fileApi.deleteFileFromQuestion(fileKey);
      commit('DELETE_FILE_FROM_QUESTION', { questionKey, fileKey });
    }),

    addAnswer: saveWrap(async ({ commit }, { questionKey }) => {
      const payload = {
        answer: {
          nameTagKey: '',
          sequence: 0,
          FK_answerTypeKey: 1,
          FK_questionKey: questionKey,
        },
      };
      const newAnswer = await answerApi.addAnswer({ payload });
      const parsedNewAnswer = {
        ...newAnswer,
        answerToSkill: {},
        dependants: {},
      };
      commit('ADD_ANSWER', { questionKey, answer: parsedNewAnswer });
      return parsedNewAnswer;
    }),
    saveAnswer: saveWrap(async (context, { answerKey, answer }) => {
      await answerApi.updateAnswer({ answerKey, answer });
    }),
    deleteAnswer: saveWrap(
      async ({ commit }, { questionKey, answerKey, dependants, linkedSkills }) => {
        await answerApi.deleteAnswer({ answerKey });
        commit('DELETE_ANSWER', { questionKey, answerKey });

        // Delete relationships
        Object.keys(dependants).forEach((questionKey) => {
          commit('DELETE_QUESTION_DEPENDENCY', { questionKey, answerKey });
        });
        Object.keys(linkedSkills).forEach((skillKey) => {
          commit(
            'editMode/skills/DELETE_ANSWER_FROM_SKILL',
            { skillKey, answerKey },
            { root: true }
          );
        });
      }
    ),
    addSkillToAnswer: saveWrap(async ({ commit }, { skillKey, questionKey, answerKey, points }) => {
      await answerApi.addSkillToAnswer({ payload: { skillKey, answerKey, points } });
      commit('ADD_SKILL_TO_ANSWER', { skillKey, questionKey, answerKey, points });
      commit('editMode/skills/ADD_ANSWER_TO_SKILL', { skillKey, answerKey }, { root: true });
    }),
    updateSkillToAnswer: saveWrap(
      async ({ commit }, { skillKey, questionKey, answerKey, points }) => {
        await answerApi.updateSkillToAnswer({ payload: { skillKey, answerKey, points } });
        commit('UPDATE_SKILL_TO_ANSWER', { skillKey, questionKey, answerKey, points });
      }
    ),
    deleteSkillFromAnswer: saveWrap(async ({ commit }, { skillKey, questionKey, answerKey }) => {
      await answerApi.deleteSkillFromAnswer({ payload: { skillKey, answerKey } });
      commit('DELETE_SKILL_FROM_ANSWER', { skillKey, questionKey, answerKey });
      commit('editMode/skills/DELETE_ANSWER_FROM_SKILL', { skillKey, answerKey }, { root: true });
    }),
    addAnswerDependant: saveWrap(async ({ commit }, { answerKey, depKey, questionKey, timer }) => {
      await answerApi.addAnswerDependant({
        payload: { FK_answerKey: answerKey, FK_questionKey: depKey, timer },
      });
      commit('ADD_ANSWER_DEPENDANT', { answerKey, depKey, questionKey, timer });
      commit('ADD_QUESTION_DEPENDENCY', { questionKey: depKey, answerKey, timer });
    }),
    updateAnswerDependant: saveWrap(
      async ({ commit }, { answerKey, depKey, questionKey, timer }) => {
        await answerApi.updateAnswerDependant({
          payload: { FK_answerKey: answerKey, FK_questionKey: depKey, timer },
        });
        commit('UPDATE_ANSWER_DEPENDANT', { answerKey, depKey, questionKey, timer });
        commit('UPDATE_QUESTION_DEPENDENCY', { questionKey: depKey, answerKey, timer });
      }
    ),
    deleteAnswerDependant: saveWrap(async ({ commit }, { answerKey, depKey, questionKey }) => {
      await answerApi.deleteAnswerDependant({
        payload: { FK_answerKey: answerKey, FK_questionKey: depKey },
      });
      commit('DELETE_ANSWER_DEPENDANT', { answerKey, depKey, questionKey });
      commit('DELETE_QUESTION_DEPENDENCY', { questionKey: depKey, answerKey });
    }),
  },

  mutations: {
    INITIALIZE_QUESTIONS: (state, { questions, questionGroup, characters }) => {
      const normalizedQuestionGroups = keyBy(questionGroup, 'questionGroupKey');

      const normalizedCharacters = keyBy(characters, 'authorKey');

      const parsedQuestions = questions.map((q) => {
        const tagKeys = q.tags.map((tag) => tag.tagKey);
        return {
          ...q,
          dependsOn: keyBy(q.dependsOn, 'FK_answerKey'),
          files: keyBy(
            q.fileToQuestion.map((ftq) => ftq.file),
            'fileKey'
          ),
          tags: new Set(tagKeys),
          uniqueKey: getKey(
            q,
            normalizedQuestionGroups[q.FK_questionGroupKey],
            normalizedCharacters[q.FK_authorKey]
          ),
        };
      });
      // TODO: move the dependency compilation to backend
      const questionsWithDependants = parsedQuestions.map((q) => {
        const answersWithDependants = q.answers.map((a) => {
          const answerDependants = parsedQuestions
            .filter(({ dependsOn }) => a.answerKey in dependsOn)
            .map((q) => q.dependsOn[a.answerKey]);
          return {
            ...a,
            answerToSkill: keyBy(a.answerToSkill, 'FK_skillKey'),
            dependants: keyBy(answerDependants, 'FK_questionKey'),
          };
        });

        return {
          ...q,
          answers: keyBy(answersWithDependants, 'answerKey'),
        };
      });

      state.questions = keyBy(questionsWithDependants, 'questionKey');
      state.questionGroups = keyBy(normalizedQuestionGroups, 'questionGroupKey');
    },

    ADD_QUESTION: (state, { questionKey, newQuestion, newQuestionGroup, character }) => {
      const tagKeys = newQuestion.tags ? newQuestion.tags.map((tag) => tag.tagKey) : [];

      const answersWithDependants = newQuestion.answers
        ? newQuestion.answers.map((a) => {
            const answerDependants = Object.values(state.questions)
              .filter(({ dependsOn }) => a.answerKey in dependsOn)
              .map((q) => q.dependsOn[a.answerKey]);
            return {
              ...a,
              answerToSkill: keyBy(a.answerToSkill, 'FK_skillKey'),
              dependants: keyBy(answerDependants, 'FK_questionKey'),
            };
          })
        : [];

      const parsedQuestion = {
        ...newQuestion,
        tags: new Set(tagKeys),
        dependsOn: keyBy(newQuestion.dependsOn, 'FK_answerKey'),
        files: keyBy(newQuestion.files, 'fileKey'),
        uniqueKey: getKey(newQuestion, newQuestionGroup, character),
        answers: keyBy(answersWithDependants, 'answerKey'),
      };

      Vue.set(state.questions, questionKey, parsedQuestion);
    },

    UPDATE_QUESTION: (state, { questionKey, payload, character }) => {
      const targetQuestion = state.questions[questionKey];
      Object.entries(payload).forEach(([questionAttr, val]) => {
        if (questionAttr in targetQuestion) Vue.set(targetQuestion, questionAttr, val);
      });

      const questionGroup = state.questionGroups[payload.FK_questionGroupKey];
      const uniqueKey = getKey(targetQuestion, questionGroup, character);
      Vue.set(targetQuestion, 'uniqueKey', uniqueKey);
    },

    DELETE_QUESTION: (state, { questionKey }) => {
      Vue.delete(state.questions, questionKey);
    },

    ADD_QUESTION_GROUP: (state, { questionGroupKey, newQuestionGroup }) => {
      Vue.set(state.questionGroups, questionGroupKey, newQuestionGroup);
    },

    ADD_FILE_TO_QUESTION: (state, { questionKey, newFile }) => {
      Vue.set(state.questions[questionKey].files, newFile.fileKey, newFile);
    },

    UPDATE_FILE_TO_QUESTION: (state, { questionKey, newFile }) => {
      Vue.set(state.questions[questionKey].files, newFile.fileKey, newFile);
    },

    DELETE_FILE_FROM_QUESTION: (state, { questionKey, fileKey }) => {
      Vue.delete(state.questions[questionKey].files, fileKey);
    },

    ADD_TAG_TO_QUESTION: (state, { questionKey, tagKey }) => {
      const targetQuestion = state.questions[questionKey];

      const newTags = new Set(targetQuestion.tags);
      newTags.add(tagKey);
      Vue.set(targetQuestion, 'tags', newTags);
    },

    DELETE_TAG_FROM_QUESTION: (state, { questionKey, tagKey }) => {
      const targetQuestion = state.questions[questionKey];

      const newTags = new Set(targetQuestion.tags);
      newTags.delete(tagKey);
      Vue.set(targetQuestion, 'tags', newTags);
    },

    ADD_QUESTION_DEPENDENCY: (state, { questionKey, answerKey, timer }) => {
      const targetQuestion = state.questions[questionKey];
      Vue.set(targetQuestion.dependsOn, answerKey, {
        FK_questionKey: questionKey,
        FK_answerKey: answerKey,
        timer,
      });
    },

    UPDATE_QUESTION_DEPENDENCY: (state, { questionKey, answerKey, timer }) => {
      const targetDependency = state.questions[questionKey].dependsOn[answerKey];

      Vue.set(targetDependency, 'timer', timer);
    },

    DELETE_QUESTION_DEPENDENCY: (state, { questionKey, answerKey }) => {
      const targetQuestion = state.questions[questionKey];

      Vue.delete(targetQuestion.dependsOn, answerKey);
    },

    CLEAR_ANSWERS_FROM_QUESTION: (state, { questionKey }) => {
      Vue.set(state.questions[questionKey], 'answers', {});
    },

    ADD_ANSWER: (state, { questionKey, answer }) => {
      const targetQuestion = state.questions[questionKey];

      Vue.set(targetQuestion.answers, answer.answerKey, answer);
    },

    UPDATE_ANSWER: (state, { questionKey, answerKey, answer }) => {
      const targetAnswer = state.questions[questionKey].answers[answerKey];

      Object.entries(answer).forEach(([answerAttr, val]) => {
        if (answerAttr in targetAnswer) Vue.set(targetAnswer, answerAttr, val);
      });
    },

    DELETE_ANSWER: (state, { questionKey, answerKey }) => {
      const targetQuestion = state.questions[questionKey];

      Vue.delete(targetQuestion.answers, answerKey);
    },

    ADD_SKILL_TO_ANSWER: (state, { skillKey, questionKey, answerKey, points }) => {
      const targetAnswer = state.questions[questionKey].answers[answerKey];

      Vue.set(targetAnswer.answerToSkill, skillKey, {
        FK_skillKey: skillKey,
        FK_answerKey: answerKey,
        points,
      });
    },

    UPDATE_SKILL_TO_ANSWER: (state, { skillKey, questionKey, answerKey, points }) => {
      const targetLinkedSkill =
        state.questions[questionKey].answers[answerKey].answerToSkill[skillKey];

      Vue.set(targetLinkedSkill, 'points', points);
    },

    DELETE_SKILL_FROM_ANSWER: (state, { skillKey, questionKey, answerKey }) => {
      const targetAnswer = state.questions[questionKey].answers[answerKey];

      Vue.delete(targetAnswer.answerToSkill, skillKey);
    },

    ADD_ANSWER_DEPENDANT: (state, { answerKey, depKey, questionKey, timer }) => {
      const targetAnswer = state.questions[questionKey].answers[answerKey];
      Vue.set(targetAnswer.dependants, depKey, {
        FK_questionKey: depKey,
        FK_answerKey: answerKey,
        timer,
      });
    },

    UPDATE_ANSWER_DEPENDANT: (state, { answerKey, depKey, questionKey, timer }) => {
      const targetDependant = state.questions[questionKey].answers[answerKey].dependants[depKey];
      Vue.set(targetDependant, 'timer', timer);
    },

    DELETE_ANSWER_DEPENDANT: (state, { answerKey, depKey, questionKey }) => {
      const targetAnswer = state.questions[questionKey].answers[answerKey];
      Vue.delete(targetAnswer.dependants, depKey);
    },
  },
};
